import {isLoggedIn} from '@github-ui/client-env'
import {ReactionViewerRelay} from '@github-ui/reaction-viewer/ReactionViewerRelay'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useSyncedState} from '@github-ui/use-synced-state'
import {clsx} from 'clsx'
import type React from 'react'
import {Suspense, useCallback, useRef} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'
import {useFragment, useRelayEnvironment} from 'react-relay/hooks'

import {CLASS_NAMES} from '../../constants/dom-elements'
import {TEST_IDS} from '../../constants/test-ids'
import {VALUES} from '../../constants/values'
import {getQuotedText} from '../../utils/quotes'
import {type CommentAuthorType, CommentAuthorTypes} from '../ActivityHeader'
import {CommentLoading} from '../CommentLoading'
import type {IssueCommentViewerCommentRow$key} from './__generated__/IssueCommentViewerCommentRow.graphql'
import type {IssueCommentViewerGhostUserQuery as IssueCommentViewerGhostUserQueryType} from './__generated__/IssueCommentViewerGhostUserQuery.graphql'
import type {IssueCommentViewerMarkdownViewer$key} from './__generated__/IssueCommentViewerMarkdownViewer.graphql'
import type {IssueCommentViewerReactable$key} from './__generated__/IssueCommentViewerReactable.graphql'
import type {
  IssueCommentViewerRefetchQuery,
  IssueCommentViewerRefetchQuery$data,
} from './__generated__/IssueCommentViewerRefetchQuery.graphql'
import {IssueCommentHeader} from './IssueCommentHeader'
import styles from './IssueCommentViewer.module.css'
import {IssueMarkdownViewer} from './IssueMarkdownViewer'
import {commitUpdateIssueCommentBodyMutation} from './mutations/update-issue-comment-body-mutation'
import {RefreshVideoWrapper} from './RefreshVideoWrapper'

type Props = {
  anchorBaseUrl?: string
  comment: IssueCommentViewerCommentRow$key
  reactable: IssueCommentViewerReactable$key
  setIsEditing: () => void
  onReply: (quotedComment: string) => void
  onLinkClick?: (event: MouseEvent) => void
  refAttribute?: React.RefObject<HTMLDivElement | null>
  highlightedCommentId?: string
  navigate: (url: string) => void
  relayConnectionIds?: string[]
  commentSubjectAuthorLogin?: string
  createdViaEmail?: boolean
  hasDisabledActions?: boolean
  showCopilotMenuItem?: boolean
}

type InternalProps = Props & {
  commentAuthorLogin: string
  avatarUrl: string
  commentAuthorType?: CommentAuthorType
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueCommentFragment = graphql`
  fragment IssueCommentViewerCommentRow on IssueComment {
    id
    databaseId
    ...IssueCommentViewerMarkdownViewer
    ...IssueCommentHeader
    author {
      avatarUrl
      login
      __typename
    }
    issue {
      id
      locked
    }
    body
    isHidden: isMinimized
    viewerCanUpdate
    pendingMinimizeReason
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueCommentViewerRefetchQuery = graphql`
  query IssueCommentViewerRefetchQuery($id: ID!) {
    node(id: $id) {
      ... on IssueComment {
        bodyHTML(unfurlReferences: true)
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueCommentViewerMarkdownViewer = graphql`
  fragment IssueCommentViewerMarkdownViewer on IssueComment {
    id
    body
    bodyHTML(unfurlReferences: true)
    bodyVersion
    viewerCanUpdate
  }
`

function isCommentAuthorType(type: string): type is CommentAuthorType {
  return (Object.values(CommentAuthorTypes) as readonly string[]).includes(type)
}

function convertCommentAuthorType(type: string | undefined): CommentAuthorType | undefined {
  return type && isCommentAuthorType(type) ? type : undefined
}

export function IssueCommentViewer({comment, ...rest}: Props) {
  const data = useFragment(IssueCommentFragment, comment)
  const withAuthor = data.author != null
  return withAuthor ? (
    <IssueCommentViewerInternal
      comment={comment}
      {...rest}
      commentAuthorLogin={data.author.login}
      commentSubjectAuthorLogin={rest.commentSubjectAuthorLogin}
      avatarUrl={data.author.avatarUrl}
      commentAuthorType={convertCommentAuthorType(data.author.__typename)}
    />
  ) : (
    <Suspense fallback={<CommentLoading />}>
      <IssueCommentViewerInternalWithoutAuthor comment={comment} {...rest} />
    </Suspense>
  )
}

function IssueCommentViewerInternalWithoutAuthor(props: Props) {
  const data = useLazyLoadQuery<IssueCommentViewerGhostUserQueryType>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      query IssueCommentViewerGhostUserQuery($ghostLogin: String!) {
        user(login: $ghostLogin) {
          login
          avatarUrl
          __typename
        }
      }
    `,
    {ghostLogin: VALUES.ghostUser.login},
    {fetchPolicy: 'store-or-network'},
  )
  if (data.user == null) {
    throw new Error('Ghost user not found')
  }
  return (
    <IssueCommentViewerInternal
      {...props}
      commentAuthorLogin={data.user.login}
      avatarUrl={data.user.avatarUrl}
      commentAuthorType={data.user.__typename}
      createdViaEmail={props.createdViaEmail}
    />
  )
}

function IssueCommentViewerInternal({
  anchorBaseUrl,
  comment,
  setIsEditing,
  onLinkClick,
  onReply,
  refAttribute,
  commentAuthorLogin,
  avatarUrl,
  highlightedCommentId,
  navigate,
  relayConnectionIds,
  commentSubjectAuthorLogin,
  reactable,
  commentAuthorType,
  createdViaEmail = false,
  hasDisabledActions = false,
  showCopilotMenuItem = false,
}: InternalProps) {
  const commentData = useFragment(IssueCommentFragment, comment)
  const reactionData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueCommentViewerReactable on Reactable {
        ...ReactionViewerRelayGroups
      }
    `,
    reactable,
  )
  const highlighted = String(commentData.databaseId) === highlightedCommentId
  const commentHidden = !!commentData.pendingMinimizeReason || commentData.isHidden
  const [isMinimized, setIsMinimized] = useSyncedState(commentHidden)

  const handleOnReply = (quotedText?: string) => {
    onReply(quotedText || getQuotedText(commentData.body))
  }

  const commentRef = useRef<HTMLDivElement>(null)
  const canReact = isLoggedIn() && !commentData.issue.locked

  return (
    <div className={clsx(CLASS_NAMES.issueComment, styles.IssueCommentLayout)} ref={commentRef}>
      <div
        data-testid={TEST_IDS.commentViewerOuterBox(commentData.id)}
        ref={highlighted ? refAttribute : null}
        className={styles.IssueCommentContent}
      >
        <IssueCommentHeader
          anchorBaseUrl={anchorBaseUrl}
          comment={commentData}
          commentAuthorLogin={commentAuthorLogin}
          navigate={navigate}
          editComment={setIsEditing}
          onReplySelect={handleOnReply}
          isMinimized={isMinimized}
          onMinimize={setIsMinimized}
          avatarUrl={avatarUrl}
          commentRef={commentRef}
          relayConnectionIds={relayConnectionIds}
          commentSubjectAuthorLogin={commentSubjectAuthorLogin}
          commentAuthorType={commentAuthorType}
          hasDisabledActions={hasDisabledActions}
          showCopilotMenuItem={showCopilotMenuItem}
        />
        {(!commentHidden || !isMinimized) && (
          <div className={styles.IssueCommentBody}>
            <CommentMarkdownViewer comment={commentData} onLinkClick={onLinkClick} createdViaEmail={createdViaEmail} />
            <ReactionViewerRelay subjectId={commentData.id} reactionGroups={reactionData} canReact={canReact} />
          </div>
        )}
      </div>
    </div>
  )
}

type CommentMarkdownViewerProps = {
  comment: IssueCommentViewerMarkdownViewer$key
  onLinkClick?: (event: MouseEvent) => void
  createdViaEmail: boolean
}

function CommentMarkdownViewer({comment, onLinkClick, createdViaEmail}: CommentMarkdownViewerProps) {
  const {id, bodyVersion, bodyHTML, viewerCanUpdate, body} = useFragment(IssueCommentViewerMarkdownViewer, comment)
  const environment = useRelayEnvironment()
  const html = (bodyHTML ?? '') as SafeHTMLString

  const issueCommentBodyRef = useRef<HTMLDivElement>(null)

  const onSave = useCallback(
    (newBody: string, onCompleted: () => void, onError: () => void) => {
      commitUpdateIssueCommentBodyMutation({
        environment,
        input: {id, body: newBody, bodyVersion},
        onCompleted,
        onError,
      })
    },
    [bodyVersion, environment, id],
  )

  const getHTML = useCallback(
    (fetchResult: Awaited<IssueCommentViewerRefetchQuery$data> | undefined): string | undefined => {
      return fetchResult?.node?.bodyHTML
    },
    [],
  )

  return (
    <RefreshVideoWrapper<IssueCommentViewerRefetchQuery>
      bodyHTML={html}
      bodyRef={issueCommentBodyRef}
      id={id}
      query={IssueCommentViewerRefetchQuery}
      getHTML={getHTML}
    >
      <IssueMarkdownViewer
        html={html}
        markdown={body}
        viewerCanUpdate={viewerCanUpdate}
        onSave={onSave}
        onLinkClick={createdViaEmail ? undefined : onLinkClick}
        createdViaEmail={createdViaEmail}
        issueCommentBodyRef={issueCommentBodyRef}
      />
    </RefreshVideoWrapper>
  )
}

try{ IssueCommentViewer.displayName ||= 'IssueCommentViewer' } catch {}
try{ IssueCommentViewerInternalWithoutAuthor.displayName ||= 'IssueCommentViewerInternalWithoutAuthor' } catch {}
try{ IssueCommentViewerInternal.displayName ||= 'IssueCommentViewerInternal' } catch {}
try{ CommentMarkdownViewer.displayName ||= 'CommentMarkdownViewer' } catch {}